import { City, CommonCode } from "@prisma/client";
import { Method } from "~/enums";
import { CityDto, CodeDto, DongDto, SigunguDto } from "~/models/dto-types";
import { useLocalStorage } from "@vueuse/core";
import { useEquipStore } from "./equipStore";
import Swal from "sweetalert2";

interface allCommonCode {
	[x: string]: CommonCode[];
}

// Map을 일반 객체로 변환하는 유틸리티 함수
function mapToObject<T>(map: Map<string, T>): Record<string, T> {
	const obj: Record<string, T> = {};
	for (const [key, value] of map.entries()) {
		obj[key] = value;
	}
	return obj;
}

// 중첩 Map을 일반 객체로 변환
function nestedMapToObject(map: Map<string, Map<string, CodeDto>>): Record<string, Record<string, CodeDto>> {
	const obj: Record<string, Record<string, CodeDto>> = {};
	for (const [category, codeMap] of map.entries()) {
		obj[category] = mapToObject(codeMap);
	}
	return obj;
}

// 일반 객체를 Map으로 변환하는 유틸리티 함수
function objectToMap<T>(obj: Record<string, T>): Map<string, T> {
	const map = new Map<string, T>();
	for (const [key, value] of Object.entries(obj)) {
		map.set(key, value);
	}
	return map;
}

// 중첩 객체를 Map으로 변환
function objectToNestedMap(obj: Record<string, Record<string, CodeDto>>): Map<string, Map<string, CodeDto>> {
	const map = new Map<string, Map<string, CodeDto>>();
	for (const [category, codeObj] of Object.entries(obj)) {
		map.set(category, objectToMap(codeObj));
	}
	return map;
}

export const useCommonCodeStore = defineStore("commonCodeStore", {
	
	state: () => ({
		codeCategories: useLocalStorage("codeCategories", [] as string[]),
		allCommonCode: useLocalStorage("allCommonCode", {} as allCommonCode),
		// 메모리에서 사용할 Map (성능 최적화)
		mapCommonCode: new Map<string, Map<string, CodeDto>>(),
		// localStorage에 저장할 일반 객체 (직렬화 가능)
		mapCommonCodeStorage: useLocalStorage("mapCommonCodeStorage", {} as Record<string, Record<string, CodeDto>>),
		cities: useLocalStorage("cities", [] as CityDto[]),
		sigungus: useLocalStorage("sigungus", [] as SigunguDto[]),
		dongs: useLocalStorage("dongs", [] as DongDto[]),
		isInitializing: false, // 초기화 중인지 확인하는 플래그
		initPromise: null as Promise<any> | null, // 초기화 Promise 캐싱
	}),
	getters: {},
	actions: {
    async init(force = false){
      // 이미 초기화 중이면 기존 Promise 반환
      if (!force && this.isInitializing && this.initPromise) {
        return this.initPromise;
      }
      
      // localStorage에서 데이터 복원 시도
      if (!force && this.mapCommonCode.size === 0 && Object.keys(this.mapCommonCodeStorage).length > 0) {
        try {
          this.mapCommonCode = objectToNestedMap(this.mapCommonCodeStorage);
          // 복원 성공 시 API 호출 생략
          if (this.mapCommonCode.size > 0) {
            return Promise.resolve();
          }
        } catch (error) {
          console.error('Failed to restore mapCommonCode from localStorage:', error);
          // 복원 실패 시 계속 진행하여 API 호출
        }
      }
      
      // 이미 데이터가 있으면 초기화하지 않음
      if (!force && this.mapCommonCode.size > 0) {
        return Promise.resolve();
      }
      
      // 초기화 시작
      this.isInitializing = true;
      this.initPromise = this.getInitAllCodeMap().finally(() => {
        this.isInitializing = false;
        this.initPromise = null;
      });
      
      return this.initPromise;
    },

		getEngSourceName(engSource: string) {
			let name = "pv";

			let engSourceInfo = this.getMapCode("engSource", engSource);
			switch (engSourceInfo?.codeName) {
				case "태양광": {
					name = "pv";
					break;
				}
				case "태양열": {
					name = "solarheat";
					break;
				}
				case "지열": {
					name = "geo";
					break;
				}
				case "연료전지": {
					name = "fuelcell";
					break;
				}
				case "풍력": {
					name = "wf";
					break;
				}
				case "ESS": {
					name = "ess";
					break;
				}
				default: {
					break;
				}
			}

			return name;
		},

		async getCodeCategories() {
			const res = await useFetch("/api/v1/common/code-categories", { method: Method.GET });
			// this.device = isMobile

			this.codeCategories = res.data.value as string[];
		},
		getMapCode(category: string, cd: string) {
			// if(Object.keys(this.mapCommonCode).length === 0){
			// 	// 비동기 초기화를 백그라운드에서 실행
			// 	this.init();
			// }

      if(cd){
        return this.mapCommonCode?.get(category)?.get(cd as string) as CodeDto;
      } 
		},
    getMapCodeList(category: string) {
      // if(Object.keys(this.mapCommonCode).length === 0){
			// 	// 비동기 초기화를 백그라운드에서 실행
			// 	this.init();
			// }

			if (!this.mapCommonCode.has(category)) return;
      
      return this.mapCommonCode?.get(category) as Map<string, CodeDto>;
		},
    getMapCodeToArray(category: string){
      const mapCodeList = this.getMapCodeList(category);
      if (!mapCodeList) return [];
      
      let result = Array.from(mapCodeList, ([key,value]) => ({key, value}));
      return result;
    },
		getMapCodeToPlainArray(category: string){
			const array = this.getMapCodeToArray(category);
			return array.map(v => v.value);
		},
	async getInitAllCodeMap() {
		const res = await useFetch<CommonCode[]>("/api/v1/common/code", { method: Method.GET });
		const categoriesData = res.data.value as CommonCode[];

      // 메모리에서 사용할 Map 구성
      this.mapCommonCode = new Map<string, Map<string, CodeDto>>();

			for (let i = 0; i < categoriesData.length; i++) {
				if (!this.mapCommonCode.has(categoriesData[i].category)) {
          this.mapCommonCode.set(categoriesData[i].category, new Map());
				}

        this.mapCommonCode.get(categoriesData[i].category)?.set(categoriesData[i].code, categoriesData[i]);
			}

			// localStorage에 일반 객체로 저장 (직렬화 가능)
			this.mapCommonCodeStorage = nestedMapToObject(this.mapCommonCode);

			return this.mapCommonCode;
		},

		async getCities() {
      const equipStore = useEquipStore();

			if(this.cities.length <= 0){
				const res = await useFetch<City[]>("/api/v1/common/region/cities", { method: Method.GET });
				this.cities = res.data.value as City[];
			}

			return this.cities;



			// for (let i = 0; i < (res.data.value as City[]).length; i++) {
      //   let cityCode = (res.data.value as City[])[i].cityCode


			// 	if (equipStore.plainEquipList.findIndex(v => v.dongCode?.substring(0,2) === cityCode) !== -1) {
			// 		this.cities.push((res.data.value as City[])[i]);
			// 	}
			// }


			// for(let i = 0; i < this.cities.length; i++){
			//   await this.getSigungu(this.cities[i].cityCode);
			// }

		},
    getCode(category: string, cd: string | undefined = undefined) {
			if (!this.allCommonCode[category]) return;

			let result = this.allCommonCode[category].find((v) => v.code.toLowerCase() === cd?.toLowerCase()) as CommonCode;
			// result['eventStatus']

			return result;
		},


		async getSigungu(cityCode: string) {

			const res = await useFetch<SigunguDto[]>(`/api/v1/common/region/cities/${cityCode}/sigungus`);

			this.sigungus = res.data.value as SigunguDto[];

			// for (let i = 0; i < sigungus.length; i++) {
			// 	if (this.sigungus.findIndex((v) => v.sigunguCode === sigungus[i].sigunguCode) === -1) {
			// 		this.sigungus.push(sigungus[i]);
			// 	}
			// }

			// for(let i = 0; i < this.sigungus.length; i++){
			//   this.getDongs(this.sigungus[i].cityCode as string, this.sigungus[i].sigunguCode);
			// }

			return this.sigungus;
		},

		async getDongs(cityCode: string, sigunguCode: string) {
			const res = await useFetch<DongDto[]>(`/api/v1/common/region/cities/${cityCode}/sigungus/${sigunguCode}/dongs`);
			this.dongs = res.data.value as DongDto[];
			return this.dongs;
		},

		async getCity(cityCode: string){
			const res = await useFetch<CityDto>(`/api/v1/common/region/cities/${cityCode}`);
			return res.data.value;
		},
		async getCitySigungu(cityCode: string, sigunguCode: string){
			const res = await useFetch<SigunguDto>(`/api/v1/common/region/cities/${cityCode}/sigungus/${sigunguCode}`);
			return res.data.value;
		},

		async getDong(cityCode: string, sigunguCode: string, dongCode: string){
			const res = await useFetch<DongDto[]>(`/api/v1/common/region/cities/${cityCode}/sigungus/${sigunguCode}/dongs/${dongCode}`);
			return res.data.value
		},

		async getCompanyInfo(category: string, syskey: string){
			const res = await useFetch(`/api/v1/common/companyaddr`, {method: Method.GET, query: {
				category: category,
				syskey: syskey
			}});

			return res.data.value;
		},
		async getCompanysList(kind: string){
			try{
				const res = await useCustomFetch(`/api/v1/common/companys`, {method: Method.GET, query:{
					kind: kind
				}});

				if(res.data.value){
					return res.data.value
				} else {
					throw new Error(res.error.value?.data.message);
				}
			}catch(e){
				Swal.fire({
      		html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })		
			}
		},
		async getCompanyManagersList(kind: string, companySeq: string){
			try{
				const res = await useCustomFetch(`/api/v1/common/companymanagers`, {method: Method.GET, query: {
					kind: kind,
					companySeq: companySeq
				}});
				
				if(res.data.value){
					return res.data.value
				} else {
					throw new Error(res.error.value?.data.message);
				}

			}catch(e){
				Swal.fire({
      		html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })	
			}
		}
	},
});
